import { decode } from 'html-entities';

/**
 * Source content (CMS, PIM) reaches us with HTML entities already in it, and in
 * places it has been escaped more than once (`&amp;nbsp;`). Decoding repeatedly
 * until the value stops changing collapses those layers back to real characters.
 */
const MAX_DECODE_PASSES = 5;

export const decodeHtmlEntities = (value: string): string => {
  let current = value;

  for (let pass = 0; pass < MAX_DECODE_PASSES; pass += 1) {
    const decoded = decode(current);

    if (decoded === current) {
      return current;
    }

    current = decoded;
  }

  return current;
};

const BLOCK_LEVEL_TAG =
  /<\/?(?:address|article|aside|blockquote|br|dd|div|dl|dt|fieldset|figcaption|figure|footer|form|h[1-6]|header|hr|li|main|nav|ol|p|pre|section|table|tbody|td|tfoot|th|thead|tr|ul)\b[^>]*>/gi;
const ANY_TAG = /<[^>]*>/g;
const LOOKS_LIKE_MARKUP = /<[a-z!/][^>]*>/i;
/** Everything ASCII-whitespace except the line feed - U+00A0 must survive. */
const INLINE_WHITESPACE = /[ \t\f\v\r]+/g;

const MAX_STRIP_PASSES = 3;

const stripTags = (value: string): string =>
  value.replace(BLOCK_LEVEL_TAG, '\n').replace(ANY_TAG, '');

/**
 * `schema.org/description` is typed `Text`, so the JSON-LD value has to be plain
 * text - no markup, no entities.
 *
 * Tags are stripped before entities are decoded, otherwise `&lt;script&gt;`
 * written as content would turn into markup and then be removed. Because the CMS
 * may hand us content that is itself double-escaped, the pair is repeated until
 * no markup is left.
 */
export const htmlToPlainText = (value: string): string => {
  let current = value;

  for (let pass = 0; pass < MAX_STRIP_PASSES; pass += 1) {
    const stripped = decodeHtmlEntities(stripTags(current));

    if (stripped === current) {
      break;
    }

    current = stripped;

    if (!LOOKS_LIKE_MARKUP.test(current)) {
      break;
    }
  }

  return current
    .replace(INLINE_WHITESPACE, ' ')
    .split('\n')
    .map((line) => line.trim())
    .join('\n')
    .replace(/\n{2,}/g, '\n')
    .trim();
};

/**
 * JSON has no concept of HTML entities (RFC 8259 section 7). Inside a
 * `<script type="application/ld+json">` block the only character that needs an
 * HTML-motivated escape is `<`, and only because of the `</script` sequence -
 * everything else is escaped by the JSON serialiser itself.
 */
const jsonLdReplacer = (_key: string, value: unknown) => {
  if (value === null) {
    return undefined;
  }

  if (typeof value === 'string') {
    return decodeHtmlEntities(value);
  }

  return value;
};

export const serializeJsonLd = (item: unknown): string =>
  JSON.stringify(item, jsonLdReplacer).replace(/</g, '\\u003c');
